import { Col, Row, Typography } from "antd";
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";

import { ClearFilters } from "./components/ClearFilters";
import { Facets } from "./components/Facets";
import { NoSearchResults } from "./components/NoSearchResults";
import { PrimaryControls } from "./components/PrimaryControls";
import { SearchResults } from "./components/SearchResults";
import {
  ColControls,
  ControlsContainer,
  JobBoardContainer,
  JobBoardNoResultsContainer,
  JobBoardResultsContainer,
  JobBoardResultsSection,
  SecondaryControlsRow,
} from "./components/styled";
import { GUTTER_MEDIUM, QUERYSTATE_INITIAL_STATE } from "./constants";
import { queryReducer } from "./queryReducer";
import { AppletProps, JobKind, QueryState } from "./types";
import { useJobBoardData } from "./useJobBoardData";
import {
  buildSearchParamsFromState,
  extractSearchParamsFromAttributes,
} from "./utils";

import { AppletWrapper } from "~/Atom/AppletWrapper";
import { isDevelopment } from "~/env";
import { useFlag } from "~/LaunchDarkly";
import { Paths } from "~/OmnichannelApi/openapi";
import { isTruthy } from "~/utils";

const Applet: React.FC<AppletProps> = ({
  inlineSearchParams,
  searchParams,
  header: showHeader,
  headerLevel,
  instantSearch: isInstantSearch,
  primaryControls: showPrimaryControls,
  facets: showFacets,
  clearAllOption,
  searchResults: showSearchResults,
  headerText,
  locationSlug,
}) => {
  const hasAuthorization = useFlag("applet-es-job-board");

  const [facets, setFacets] =
    useState<Paths.JobsList.Responses.$200["metadata"]["facets"]>();

  // Refs for tracking render state
  const isFirstRender = useRef(true);

  // Query state management
  const [queryState, queryDispatch] = useReducer<typeof queryReducer>(
    queryReducer,
    {
      ...QUERYSTATE_INITIAL_STATE,
      ...(inlineSearchParams || searchParams),
      commuteFilter: Boolean(searchParams.commuteFilter === "true"),
      locationSlug: locationSlug || QUERYSTATE_INITIAL_STATE.locationSlug,
    }
  );

  const onInitialLoad = useCallback(
    (queryState: QueryState) => {
      const searchParams = buildSearchParamsFromState(queryState);

      if (!inlineSearchParams && !locationSlug) {
        window.history.replaceState(
          null,
          "",
          [window.location.pathname, searchParams.toString() || undefined]
            .filter(isTruthy)
            .join("?")
        );
      }
    },
    [inlineSearchParams, locationSlug]
  );

  const jobs = useJobBoardData(
    queryState,
    [
      { jobKind: JobKind.regular, pageSize: 10 },
      { jobKind: JobKind.prospect, pageSize: 5 },
    ],
    setFacets,
    onInitialLoad
  );

  const showSecondaryControls = () => {
    return showFacets || (clearAllOption && isInstantSearch);
  };

  const initiateSearch = (queryState: QueryState) => {
    if (!isInstantSearch && !showSearchResults) {
      const params = buildSearchParamsFromState(queryState);
      const queryString = params.toString();
      const url = `/careers/current-openings${
        queryString ? `?${queryString}` : ""
      }`;
      window.location.href = url;
    } else {
      jobs.initData();
    }
  };

  const noResultsMessage = () => {
    if (
      !jobs.isInitialized ||
      (jobs.isInitialized && jobs.hasJobs(JobKind.regular))
    ) {
      return null;
    }
    if (queryState.locationSlug) {
      return "No current openings at this location.";
    }
    if (!jobs.hasJobs()) {
      return "Sorry, we couldn't find any current or future openings matching your search.";
    }
    if (!jobs.hasJobs(JobKind.regular) && jobs.hasJobs(JobKind.prospect)) {
      return [
        "Sorry, we couldn't find any current openings matching your search.",
        "Check out these future openings.",
      ];
    }

    return null;
  };

  useEffect(() => {
    if (isInstantSearch || isFirstRender.current) {
      isFirstRender.current = false;
      showSearchResults && initiateSearch(queryState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("unavailable") === "true") {
      // eliminate race condition on applet mounting
      setTimeout(() => {
        window.notifyUser &&
          window.notifyUser("error", {
            message: "The opening you requested is no longer available.",
            duration: 15,
          });
      }, 500);
    }
  }, []);

  if (hasAuthorization !== true) {
    if (isDevelopment) {
      console.error("Unauthorized to access applet.");
    }

    return null;
  }

  return (
    <AppletWrapper appletName="job-board">
      <JobBoardContainer>
        {showHeader && (
          <Row gutter={GUTTER_MEDIUM}>
            <Col span={24}>
              <Typography.Title level={headerLevel}>
                {headerText}
              </Typography.Title>
            </Col>
          </Row>
        )}

        <ControlsContainer>
          {showPrimaryControls && (
            <PrimaryControls
              queryState={queryState}
              queryDispatch={queryDispatch}
              instantSearch={isInstantSearch}
              onSearchClick={() => initiateSearch(queryState)}
            />
          )}

          {showSecondaryControls() && (
            <SecondaryControlsRow>
              <ColControls>
                {showFacets && (
                  <Facets
                    queryState={queryState}
                    queryDispatch={queryDispatch}
                    facets={facets}
                  />
                )}
              </ColControls>
              <ColControls>
                {clearAllOption && isInstantSearch && (
                  <ClearFilters queryDispatch={queryDispatch} />
                )}
              </ColControls>
            </SecondaryControlsRow>
          )}
        </ControlsContainer>

        {showSearchResults && (
          <JobBoardResultsSection>
            {noResultsMessage() && (
              <JobBoardNoResultsContainer>
                <NoSearchResults
                  queryState={queryState}
                  queryDispatch={queryDispatch}
                  allowReset={!inlineSearchParams && !locationSlug}
                  message={noResultsMessage()}
                  showActions={!jobs.hasJobs()}
                />
              </JobBoardNoResultsContainer>
            )}
            <JobBoardResultsContainer>
              {(!jobs.isInitialized || jobs.hasJobs(JobKind.regular)) && (
                <SearchResults
                  title={"Current Openings"}
                  jobs={jobs.data[JobKind.regular] ?? []}
                  metadata={jobs.metadata[JobKind.regular]}
                  isLoading={
                    jobs.isLoading(JobKind.regular) || !jobs.isInitialized
                  }
                  showMoreJobs={() => jobs.loadMore(JobKind.regular)}
                />
              )}
              {jobs.isInitialized && jobs.hasJobs(JobKind.prospect) && (
                <SearchResults
                  title={"Future Openings"}
                  description={
                    "While the following openings do not represent current open roles, it allows you to express interest and share your experience with us."
                  }
                  jobs={jobs.data[JobKind.prospect] ?? []}
                  metadata={jobs.metadata[JobKind.prospect]}
                  isLoading={jobs.isLoading(JobKind.prospect)}
                  showMoreJobs={() => jobs.loadMore(JobKind.prospect)}
                />
              )}
            </JobBoardResultsContainer>
          </JobBoardResultsSection>
        )}
      </JobBoardContainer>
    </AppletWrapper>
  );
};

export const mount = (el: HTMLDivElement) => {
  const root = createRoot(el);
  const props = extractSearchParamsFromAttributes(el);
  root.render(<Applet {...props} />);
};
